/**
 * View Manager
 * 
 * Manages view lifecycle and rendering. This is Layer 2 of the two-layer allocation system.
 * 
 * Responsibilities:
 * - Manage view geometry (from Grid Allocator)
 * - Optionally map views to squares (for square-based views only)
 * - Handle views that don't use squares (DOM, canvas, WebGL directly)
 * - Coordinate rendering based on view kind
 * - Integrate with Grid Allocator (Layer 2) for square-based views
 * 
 * **Important:** Views are NOT required to use squares.
 * - Some views use squares (traditional cards)
 * - Some views use DOM directly (chat windows, forms)
 * - Some views use canvas/WebGL directly (games, shaders, visualizations)
 * - Some views use multiple rendering methods
 * 
 * Architecture:
 * - Layer 1: Viewport Allocation Integration - Coordinates allocation pipeline
 * - Layer 2: View Manager (this file) + Grid Allocator - View-to-square mapping and spatial allocation
 */

import type { ViewInstance, Process, ViewGeometry } from './process.js';
import type { GridElement } from '../grid-allocation-v2/types.js';
import { BaseViewHTML } from './base-view-html.js';
import { bus } from '../core/events.js';
import { uid } from '../utils/helpers.js';
import { log, trace } from '../services/logging/index.js';
import { processManager } from './process-manager.js';
import { ProcessPriority } from './process.js';
import { setDismissButtonHover } from '../rendering/effects.js';
import { state, setState } from '../core/state.js';

/**
 * View-to-element mapping
 * 
 * Maps a view to one or more grid elements (for grid-based views only).
 * Some views don't use grid elements at all.
 */
export interface ViewElementMapping {
  /** View ID */
  viewId: string;

  /** Process ID that owns this view */
  processId: string;

  /** Grid elements associated with this view (can be 0, 1, or many) */
  elements: GridElement[];

  /** Does this view use grid elements? */
  usesGrid: boolean;

  /** View rendering method */
  renderingMethod: 'grid' | 'dom' | 'canvas' | 'webgl' | 'hybrid' | 'custom';
}

/**
 * View Manager
 * 
 * Manages views and their rendering lifecycle.
 */
export class ViewManager {
  private viewMappings: Map<string, ViewElementMapping> = new Map();
  private registeredViews: Map<string, ViewInstance> = new Map(); // Store views directly
  private baseViewHTMLInstances: Map<string, BaseViewHTML> = new Map(); // Track BaseViewHTML instances (HTML)
  private layerElement: HTMLElement | null = null;

  constructor(layerElement: HTMLElement | null = null) {
    this.layerElement = layerElement;

    // NOTE: ViewManager no longer listens to process.view.registered
    // Views are registered directly via registerView() by ProcessContext

    // Listen for view destruction
    bus.on('process.view.destroyed', (payload) => {
      const viewId = (payload as { viewId: string }).viewId;
      this.unregisterView(viewId);
    });

    // REMOVED: process.view.needs-render listener - BaseView handles its own rendering
  }

  /**
   * Set the layer element (DOM container for views)
   */
  setLayerElement(element: HTMLElement | null): void {
    this.layerElement = element;
  }

  /**
   * Register a view for management
   * 
   * Determines rendering method based on view kind and metadata.
   */
  registerView(view: ViewInstance): void {
    const traceId = trace.start('view-manager-register');
    // Determine rendering method
    const renderingMethod = this.determineRenderingMethod(view);

    // Create view mapping
    const mapping: ViewElementMapping = {
      viewId: view.id,
      processId: view.processId || '',
      elements: [],
      usesGrid: renderingMethod === 'grid' || renderingMethod === 'hybrid',
      renderingMethod
    };

    // Grid elements removed - using horizontal positioning instead
    // No longer create grid elements

    // Handle headless views (kind: 'none') - they don't have UI
    if (view.kind === 'none') {
      // Headless view - just register it, no DOM mounting needed
      this.viewMappings.set(view.id, mapping);
      this.registeredViews.set(view.id, view);
      trace.end(traceId);
      return;
    }

    // Views must be BaseViewHTML (HTML)
    const baseViewHTMLInstance = this.getBaseViewHTMLInstanceFromView(view);

    if (!baseViewHTMLInstance) {
      throw new Error(`View ${view.id} must be a BaseViewHTML instance.`);
    }

    if (!this.layerElement) {
      // Try to find layer element if not set (fallback for early registration)
      this.layerElement = document.getElementById('layer');
      
      if (!this.layerElement) {
        throw new Error('ViewManager.layerElement must be set before registering views.');
      }
    }

    // Get or create content wrapper for horizontal scroll
    let contentWrapper = this.layerElement.querySelector('.layer-content') as HTMLElement;
    if (!contentWrapper) {
      contentWrapper = document.createElement('div');
      contentWrapper.className = 'layer-content';
      this.layerElement.appendChild(contentWrapper);
    }
    
    // Update cached reference in horizontal allocator
    import('../systems/horizontal-allocator.js').then(({ setLayerContentElement }) => {
      setLayerContentElement(contentWrapper);
    });

    // Mount the HTML view to content wrapper
    baseViewHTMLInstance.mount(contentWrapper);
    
    // CRITICAL: Prepend new views to the left (head of feed)
    // Insert at the beginning of the container (leftmost position in flex container)
    const element = baseViewHTMLInstance.getElement();
    if (element) {
      // Remove from current position if already in DOM (mount() doesn't append)
      if (element.parentElement === contentWrapper) {
        contentWrapper.removeChild(element);
      }
      // Insert at the beginning (leftmost)
      contentWrapper.insertBefore(element, contentWrapper.firstChild);
    }
    
    // Note: Order update is handled synchronously in allocateViewHorizontally()
    // to avoid race conditions. Don't update order here.
    
    this.baseViewHTMLInstances.set(view.id, baseViewHTMLInstance);

    this.viewMappings.set(view.id, mapping);
    this.registeredViews.set(view.id, view); // Store view directly for immediate access

    // CRITICAL: Trigger allocation immediately after view registration
    // This ensures DOM/canvas are created with correct allocated geometry, not placeholder geometry
    // Allocation must happen SYNCHRONOUSLY before we emit events, so canvas renders with correct size/position
    bus.emit('view.registered', {
      viewId: view.id,
      mapping: mapping
    });

    // Trigger immediate allocation to ensure atomic DOM/canvas creation
    // This ensures square and DOM/canvas are created together with correct geometry
    bus.emit('view.needs-allocation', {
      viewId: view.id
    });

    // Play view spawn sound with pan based on position (left = negative pan)
    // New views spawn on the left, so use negative pan

    trace.end(traceId);
  }

  /**
   * Unregister a view (view was destroyed)
   */
  unregisterView(viewId: string): void {
    const traceId = trace.start('view-manager-unregister');

    const mapping = this.viewMappings.get(viewId);
    if (!mapping) {
      trace.end(traceId);
      return;
    }

    // Clean up elements (ripple effect removed - no longer using canvas-based UI)

    // Unmount HTML view
    const baseViewHTMLInstance = this.baseViewHTMLInstances.get(viewId);
    if (baseViewHTMLInstance) {
      baseViewHTMLInstance.unmount();
      this.baseViewHTMLInstances.delete(viewId);
    }

    this.viewMappings.delete(viewId);
    this.registeredViews.delete(viewId);
    
    // CRITICAL: Remove from view order SYNCHRONOUSLY before reallocation
    // This ensures allocateAllViews() sees the correct order without the deleted view
    import('../core/state.js').then(({ state, setState, updateState }) => {
      const currentOrder = state.viewOrder || [];
      const filteredOrder = currentOrder.filter(id => id !== viewId);
      if (!state.viewOrder) {
        setState('viewOrder', filteredOrder);
      } else {
        updateState('viewOrder', () => filteredOrder);
      }
      
      // CRITICAL: Reallocate AFTER order is updated (synchronously in the promise)
      // This ensures allocateAllViews() sees the correct order
      import('./horizontal-allocator.js').then(({ allocateAllViews }) => {
        allocateAllViews();
      });
    });

    // Emit view unregistered event
    bus.emit('view.unregistered', {
      viewId: viewId
    });

    trace.end(traceId);
  }


  /**
   * Get BaseViewHTML instance from ViewInstance (if it's a BaseViewHTML)
   */
  private getBaseViewHTMLInstanceFromView(view: ViewInstance): BaseViewHTML | null {
    if (view.data && typeof view.data === 'object') {
      const data = view.data as any;
      if (data._baseViewInstance && data._baseViewInstance instanceof BaseViewHTML) {
        return data._baseViewInstance;
      }
    }
    return null;
  }


  /**
   * Get BaseViewHTML instance by view ID (public API)
   */
  getBaseViewHTMLInstance(viewId: string): BaseViewHTML | null {
    const instance = this.baseViewHTMLInstances.get(viewId);
    if (instance) {
      return instance;
    }

    const view = this.registeredViews.get(viewId);
    if (view) {
      return this.getBaseViewHTMLInstanceFromView(view);
    }

    return null;
  }

  /**
   * Determine rendering method for a view
   */
  private determineRenderingMethod(view: ViewInstance): ViewElementMapping['renderingMethod'] {
    // Check view kind first
    switch (view.kind) {
      case 'square':
        return 'grid';

      case 'canvas':
        // Canvas view can be canvas2d or webgl
        return 'canvas';

      case 'layer':
      case 'stack':
      case 'particle':
        // These typically use canvas/WebGL
        return 'canvas';

      case 'none':
        // No visual representation
        return 'custom';

      default:
        // Default to grid for traditional views
        return 'grid';
    }
  }

  /**
   * Create grid elements for a grid-based view
   * 
   * This maps a view to one or more grid elements based on the view's geometry,
   * size constraints, and allocation policy.
   * 
   * Size constraints are cell-based (v2 schema).
   * 
   * IMPORTANT: Always reads metadata from BaseView instance (not stored ViewInstance)
   * to ensure dynamic metadata changes are picked up.
   */
  private createElementsForView(view: ViewInstance): GridElement[] {
    // Get current metadata from BaseView instance (not stored ViewInstance)
    // This ensures dynamic metadata changes are picked up
    // We need to get it via toViewInstance() to access protected properties
    const currentViewInstance = view;
    const currentMetadata = currentViewInstance.metadata || view.metadata;

    const geometry = currentViewInstance.geometry || view.geometry;
    if (!geometry) {
      return [];
    }

    // Get grid state to convert pixel sizes to grid cells
    // Note: Grid state is accessed via the integration layer
    // For now, we'll set desiredCols/desiredRows based on constraints
    // The actual allocation will happen in GridAllocator

    const metadata = currentMetadata;
    const sizeConstraints = metadata?.sizeConstraints;

    // Convert size constraints from pixels to grid cells
    // Default grid cell size (will be updated when grid is available)
    const defaultCellW = 200;
    const defaultCellH = 200;
    const defaultGap = 10;

    // Get acceptableSizes from metadata (v2 schema)
    const acceptableSizes = sizeConstraints?.acceptableSizes || [{ cols: 1, rows: 1 }];
    
    // Get preferred position from metadata (v2 schema)
    const preferredPosition = metadata?.preferredPosition;
    
    // Page restrictions removed - app now uses single horizontal feed
    const allowedPages = undefined; // Always undefined - no page restrictions
    
    // Get priority from metadata (v2 schema)
    const priority = metadata?.priority ?? 1;

    // Create grid element (v2 schema)
    const element: GridElement = {
      id: `${view.id}-element`,
      acceptableSizes,
      preferredPosition,
      allowedPages,
      priority,
      state: {
        x: -1,  // -1 means not allocated yet
        y: -1,  // -1 means not allocated yet
        cols: 0,  // Will be set by GridAllocator
        rows: 0,  // Will be set by GridAllocator
        page: 0,  // Default to page 0
        hidden: false,
        px: {
          x: geometry.x || 0,
          y: geometry.y || 0,
          w: (geometry.width as number) || 0,
          h: (geometry.height as number) || 0
        }
      }
    };

    // Store view ID on element for reference (for proper lookup chain)
    (element as any).viewId = view.id;

    // Store processId directly on element (more reliable than parsing from viewId)
    if (view.processId) {
      (element as any).processId = view.processId;
    }

    // Store view metadata on element for reference
    if (metadata) {
      (element as any).viewMetadata = metadata;
    }

    // Store process layoutMode on element for simple layout allocation
    // Get process to access its metadata
    const process = view.processId ? processManager.getProcess(view.processId) : null;
    if (process) {
      if (process.metadata?.layoutMode) {
        (element as any).layoutMode = process.metadata.layoutMode;
        (element as any).layoutConfig = process.metadata.layoutConfig;
        console.log(`[VIEW-MANAGER] Stored layoutMode=${process.metadata.layoutMode} on element for view ${view.id}, processId=${view.processId}`);
      } else {
        console.log(`[VIEW-MANAGER] Process ${view.processId} found but no layoutMode in metadata for view ${view.id}`);
      }
    } else {
      console.warn(`[VIEW-MANAGER] Process ${view.processId} not found for view ${view.id}`);
    }

    return [element];
  }

  /**
   * Get process from element using proper lookup chain
   * 
   * Lookup chain: GridElement → viewId → ViewInstance → processId → Process
   * 
   * This is the correct way to get process from element.
   * 
   * @param element - GridElement to get process from
   * @returns Process instance, or null if not found
   */
  getProcessFromElement(element: GridElement): Process | null {
    // Step 1: Get viewId from element
    const viewId = (element as any).viewId;
    if (!viewId) return null;

    // Step 2: Get ViewInstance from viewId
    const view = this.registeredViews.get(viewId);
    if (!view) return null;

    // Step 3: Get processId from ViewInstance
    const processId = view.processId;
    if (!processId) return null;

    // Step 4: Get Process from processId
    return processManager.getProcess(processId) || null;
  }

  // REMOVED: createViewElements() - BaseView.mount() handles all DOM/canvas creation
  // REMOVED: updateViewContent() - BaseView.updateHTML() handles content updates
  // REMOVED: attachViewEventListeners() - BaseView handles event listeners internally

  /**
   * Get all grid elements for all views (for Grid Allocator)
   * 
   * This aggregates elements from all grid-based views.
   */
  getAllElements(): GridElement[] {
    // Grid elements removed - return empty array
    return [];
  }
  
  /**
   * Get all BaseViewHTML instances
   */
  getAllViews(): Map<string, BaseViewHTML> {
    return this.baseViewHTMLInstances;
  }

  /**
   * Get grid elements for a specific view
   */
  getViewElements(viewId: string): GridElement[] {
    const mapping = this.viewMappings.get(viewId);
    return mapping?.elements || [];
  }

  /**
   * Get view for an element ID (for allocation retry tracking)
   */
  getViewForElement(elementId: string): ViewInstance | null {
    for (const [viewId, mapping] of this.viewMappings.entries()) {
      if (mapping.elements.some(e => e.id === elementId)) {
        const views = this.getActiveViews();
        return views.find(v => v.id === viewId) || null;
      }
    }
    return null;
  }

  /**
   * Get element ID for a view ID (for hover state tracking)
   */
  getElementIdForView(viewId: string): string | null {
    const mapping = this.viewMappings.get(viewId);
    if (mapping?.usesGrid && mapping.elements.length > 0) {
      // Return first element ID (most views have one element)
      return mapping.elements[0].id;
    }
    return null;
  }

  /**
   * Update view geometries based on viewport allocation OR grid allocation
   * 
   * IMPORTANT: This method can be called in two contexts:
   * 1. After viewport allocation (before grid allocation) - uses viewport coordinates
   * 2. After grid allocation (via updateViewGeometriesFromSquares) - uses grid pixel coordinates (WITH gutters)
   * 
   * When grid allocation has occurred, view.geometry should already be updated with grid positions.
   * This method then syncs DOM/canvas elements to match.
   */
  /**
   * Get all active views (moved from ViewportAllocationManager)
   */
  getActiveViews(): ViewInstance[] {
    // Return views directly from registeredViews - no fragile ProcessContext lookup
    return Array.from(this.registeredViews.values());
  }

  // Track previous state for change detection
  private previousGeometries: Map<string, ViewGeometry> = new Map();
  private previousPages: Map<string, number> = new Map();
  private previousSizes: Map<string, { cols: number; rows: number }> = new Map();

  updateViewGeometries(): void {
    const views = this.getActiveViews();
    const changes = this.detectViewChanges(views);

    if (changes.length > 0) {
      // Trigger choreographed animation sequence
      import('../animation/view-animation-manager.js').then(({ viewAnimationManager }) => {
        viewAnimationManager.animateViewChanges(changes).catch((error) => {
          log.error('Animation error', { error });
          // Fallback to immediate update on error
          this.updateGeometriesImmediate(views);
        });
      });
    } else {
      // No changes - update immediately
      this.updateGeometriesImmediate(views);
    }
  }

  /**
   * Detect view changes for animation
   */
  private detectViewChanges(views: ViewInstance[]): Array<import('../animation/view-animation-manager.js').ViewChange> {
    const changes: Array<import('../animation/view-animation-manager.js').ViewChange> = [];

    for (const view of views) {
      const mapping = this.viewMappings.get(view.id);
      if (!mapping) continue;

      const baseViewHTMLInstance = this.getBaseViewHTMLInstance(view.id);
      if (!baseViewHTMLInstance) continue;

      const previousGeometry = this.previousGeometries.get(view.id);
      const previousPage = this.previousPages.get(view.id) ?? 0;
      const previousSize = this.previousSizes.get(view.id) ?? { cols: 0, rows: 0 };

      // Get current state
      let currentGeometry: ViewGeometry | null = null;
      let currentPage = 0;
      let currentSize = { cols: 0, rows: 0 };
      let isHidden = false;

      if (mapping.usesGrid && mapping.elements.length > 0) {
        const element = mapping.elements[0];
        isHidden = element.state.hidden || element.state.cols === 0 || element.state.rows === 0 || element.state.x < 0 || element.state.y < 0;
        
        if (!isHidden) {
          currentGeometry = {
            x: element.state.px.x,
            y: element.state.px.y,
            width: element.state.px.w,
            height: element.state.px.h
          };
          currentPage = element.state.page ?? 0;
          currentSize = { cols: element.state.cols, rows: element.state.rows };
        }
      } else if (view.geometry) {
        currentGeometry = {
          x: (view.geometry.x as number) || 0,
          y: (view.geometry.y as number) || 0,
          width: (view.geometry.width as number) || 0,
          height: (view.geometry.height as number) || 0
        };
        currentPage = 0;
        currentSize = { cols: 0, rows: 0 };
      }

      // Detect change type
      if (!previousGeometry) {
        // New view - start at target position, only fade in (no position animation)
        if (currentGeometry && !isHidden) {
          const viewInstance = baseViewHTMLInstance;
          if (viewInstance) {
            changes.push({
              viewId: view.id,
              view: viewInstance as any,
              previousGeometry: currentGeometry, // Start at target position (no position animation)
              newGeometry: currentGeometry,
              previousPage: -1,
              newPage: currentPage,
              previousSize: { cols: 0, rows: 0 },
              newSize: currentSize,
              changeType: 'spawn'
            });
          }
        }
      } else if (isHidden || !currentGeometry) {
        // View being dismissed/removed
        const viewInstance = baseViewHTMLInstance;
        if (viewInstance) {
          changes.push({
            viewId: view.id,
            view: viewInstance as any,
            previousGeometry,
            newGeometry: { x: 0, y: 0, width: 0, height: 0 },
            previousPage,
            newPage: -1,
            previousSize,
            newSize: { cols: 0, rows: 0 },
            changeType: 'dismiss'
          });
        }
      } else if (previousPage !== currentPage) {
        // View moved to different page
        const viewInstance = baseViewHTMLInstance;
        if (viewInstance) {
          changes.push({
            viewId: view.id,
            view: viewInstance as any,
            previousGeometry,
            newGeometry: currentGeometry,
            previousPage,
            newPage: currentPage,
            previousSize,
            newSize: currentSize,
            changeType: 'move-page'
          });
        }
      } else if (currentGeometry) {
        // View staying on same page - check size/position changes
        const sizeChanged = previousSize.cols !== currentSize.cols || previousSize.rows !== currentSize.rows;
        const positionChanged = previousGeometry.x !== currentGeometry.x || previousGeometry.y !== currentGeometry.y;
        const geometryChanged = positionChanged || 
                               previousGeometry.width !== currentGeometry.width || 
                               previousGeometry.height !== currentGeometry.height;

        if (geometryChanged) {
          const viewInstance = baseViewHTMLInstance;
          if (!viewInstance) continue;
          
          if (sizeChanged && (currentSize.cols < previousSize.cols || currentSize.rows < previousSize.rows)) {
            // Shrinking
            changes.push({
              viewId: view.id,
              view: viewInstance as any,
              previousGeometry,
              newGeometry: currentGeometry,
              previousPage,
              newPage: currentPage,
              previousSize,
              newSize: currentSize,
              changeType: 'shrink'
            });
          } else if (positionChanged) {
            // Moving
            changes.push({
              viewId: view.id,
              view: viewInstance as any,
              previousGeometry,
              newGeometry: currentGeometry,
              previousPage,
              newPage: currentPage,
              previousSize,
              newSize: currentSize,
              changeType: 'move'
            });
          } else if (sizeChanged && (currentSize.cols > previousSize.cols || currentSize.rows > previousSize.rows)) {
            // Expanding
            changes.push({
              viewId: view.id,
              view: viewInstance as any,
              previousGeometry,
              newGeometry: currentGeometry,
              previousPage,
              newPage: currentPage,
              previousSize,
              newSize: currentSize,
              changeType: 'expand'
            });
          }
        }
      }

      // Update previous state
      if (currentGeometry && !isHidden) {
        this.previousGeometries.set(view.id, currentGeometry);
        this.previousPages.set(view.id, currentPage);
        this.previousSizes.set(view.id, currentSize);
      } else {
        // View is hidden/removed - remove from tracking
        this.previousGeometries.delete(view.id);
        this.previousPages.delete(view.id);
        this.previousSizes.delete(view.id);
      }
    }

    return changes;
  }

  /**
   * Update geometries immediately (no animation)
   */
  private updateGeometriesImmediate(views: ViewInstance[]): void {
    log.debug(`[ViewManager] updateGeometriesImmediate() called for ${views.length} views`);
    for (const view of views) {
      const mapping = this.viewMappings.get(view.id);
      if (!mapping) {
        log.debug(`[ViewManager] No mapping found for view ${view.id}`);
        continue;
      }

      // CRITICAL: If view uses grid elements, use element positions (from Grid Allocator)
      // Grid Allocator converts grid coords to pixel coords WITH gutters
      if (mapping.usesGrid && mapping.elements.length > 0) {
        const element = mapping.elements[0]; // For single-element views
        const baseViewHTMLInstance = this.getBaseViewHTMLInstance(view.id);
        
        if (!baseViewHTMLInstance) {
          log.debug(`[ViewManager] No BaseViewHTML instance for ${view.id}`);
          continue;
        }

        // Check if element has been allocated
        if (element.state.hidden || element.state.cols === 0 || element.state.rows === 0 || element.state.x < 0 || element.state.y < 0) {
          // Element not allocated - hide view by moving it off-screen
          log.debug(`[ViewManager] Element ${element.id} not allocated for view ${view.id}, hiding view`);
          const hiddenGeometry = {
            x: -10000,
            y: -10000,
            width: 0,
            height: 0
          };
          baseViewHTMLInstance.updateGeometry(hiddenGeometry);
          continue;
        }

        const allocation = {
          x: element.state.px.x, // Grid-allocated pixel position (WITH gutters)
          y: element.state.px.y,
          width: element.state.px.w,
          height: element.state.px.h
        };

        log.debug(`[ViewManager] Updating geometry for ${view.id} from element ${element.id}:`, allocation);

        // Update view geometry from grid-allocated element
        if (baseViewHTMLInstance) {
          baseViewHTMLInstance.updateGeometry({
            x: allocation.x,
            y: allocation.y,
            width: allocation.width,
            height: allocation.height
          });
        }
        continue;
      }

      // Fallback: Use view geometry (for non-square views or views without squares yet)
      // View geometry is set by grid allocation or view initialization
      const allocation = view.geometry ? {
        x: (view.geometry.x as number) || 0,
        y: (view.geometry.y as number) || 0,
        width: (view.geometry.width as number) || 0,
        height: (view.geometry.height as number) || 0
      } : null;

      if (allocation) {
        // Update geometry for either BaseView or BaseViewHTML
        const baseViewHTMLInstance = this.baseViewHTMLInstances.get(view.id);
        
        if (baseViewHTMLInstance) {
          log.debug(`[ViewManager] Updating geometry for ${view.id} from view geometry (fallback):`, allocation);
          baseViewHTMLInstance.updateGeometry({
            x: allocation.x,
            y: allocation.y,
            width: allocation.width,
            height: allocation.height
          });
        } else {
          log.debug(`[ViewManager] No BaseView or BaseViewHTML instance for ${view.id} (fallback)`);
        }
      } else {
        log.debug(`[ViewManager] No allocation available for view ${view.id}`);
      }
    }
    log.debug(`[ViewManager] updateGeometriesImmediate() completed`);
  }

  // REMOVED: updateViewElementPositions() - BaseView.setGeometry() handles all DOM/canvas updates

  /**
   * Get all grid elements for all views (for Grid Allocator)
   * 
   * This aggregates elements from all grid-based views.
   * @deprecated Use getAllElements() instead
   */
  getAllSquares(): GridElement[] {
    return this.getAllElements();
  }

  /**
   * Get grid elements for a specific view
   * @deprecated Use getViewElements() instead
   */
  getViewSquares(viewId: string): GridElement[] {
    return this.getViewElements(viewId);
  }

  /**
   * Get view mapping
   */
  getViewMapping(viewId: string): ViewElementMapping | null {
    return this.viewMappings.get(viewId) || null;
  }

  /**
   * Get view for an element ID (for allocation retry tracking)
   * @deprecated Use getViewForElement() instead
   */
  getViewForSquare(elementId: string): ViewInstance | null {
    return this.getViewForElement(elementId);
  }

  /**
   * Get element ID for a view ID (for hover state tracking)
   * @deprecated Use getElementIdForView() instead
   */
  getSquareIdForView(viewId: string): string | null {
    return this.getElementIdForView(viewId);
  }

  /**
   * Update view metadata and recreate elements (for dynamic metadata changes)
   * 
   * When a view's metadata changes dynamically (e.g., size constraints),
   * this method recreates the elements with the new constraints.
   * 
   * @param viewId - The view ID to update
   */
  updateViewMetadata(viewId: string): void {
    const view = this.registeredViews.get(viewId);
    if (!view) return;

    const mapping = this.viewMappings.get(viewId);
    if (!mapping) return;

    // Get current metadata from BaseView instance (ensures we get latest changes)
    const baseViewHTMLInstance = this.getBaseViewHTMLInstance(viewId);
    if (!baseViewHTMLInstance) return;

    // Recreate elements with updated metadata
    if (mapping.usesGrid) {
      mapping.elements = this.createElementsForView(view);
    }

    // Trigger re-allocation with new elements
    bus.emit('view.needs-allocation', {
      viewId: viewId
    });
  }

  // REMOVED: renderView(), renderAllViews(), getViewElement(), getViewCanvas(), attachCanvasMouseHandlers(), setViewVisibility()
  // BaseView handles all rendering, DOM/canvas access, and visibility management
}

/**
 * Global View Manager instance
 */
export const viewManager = new ViewManager();

